import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button, Empty, Field, Icon, Input, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import { useApi, type ChangelogEntry } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths } from '../app/router.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { EmailTemplates, Outbox, PrintTemplates } from './TemplatePages.js'
import { IntegrationPage } from './IntegrationPage.js'
import { Branding, DataExports, DataImports } from './DataPages.js'
import { formatDateTime } from '../framework/format.js'
import { PermissionsPage } from './admin/PermissionsPage.js'
import { WorkflowsPage } from './admin/WorkflowsPage.js'
import { PeoplePage } from './admin/PeoplePage.js'
import { CompaniesPage, NumberingPage } from './admin/SetupPages.js'
import { DelegateRequestsPage, LoginRegisterPage } from './admin/RegisterPages.js'
import { WorkListSetup } from './admin/WorkListSetup.js'
import './admin/admin.css'

/**
 * Administration (docs/11 §User and Permission, §Setup).
 *
 * Five screens behind one hub: who is on the account, what each role may do,
 * what documents are called, who approves them, and the companies themselves.
 */

const SECTIONS = [
  { key: 'users', label: 'Users', blurb: 'Who is on this account, their type, role and data scope.' },
  { key: 'roles', label: 'Permissions', blurb: 'What each role may do with each document type, master and report, and the special actions.' },
  { key: 'register', label: 'User Login Register', blurb: 'Every attempt to sign in: who, when, from where.' },
  { key: 'numbering', label: 'Document Number Rules', blurb: 'The format and the next number for every document type.' },
  { key: 'workflows', label: 'Workflows', blurb: 'The approval steps a document goes through, and who is alerted.' },
  { key: 'companies', label: 'Companies', blurb: 'The legal entities this account keeps books for.' },
  { key: 'templates', label: 'Print Templates', blurb: 'The layout each kind of document prints in.' },
  { key: 'email', label: 'Email Templates', blurb: 'The wording of every message this system sends out.' },
  { key: 'outbox', label: 'Email Outbox', blurb: 'What has been sent, what is waiting, and what bounced.' },
  { key: 'integration', label: 'ERP/Accounting Integration', blurb: 'Which accounting system the books go to, and what is still waiting.' },
  { key: 'imports', label: 'Data Imports', blurb: 'Masters, users and revised GST rates, from a spreadsheet: download, edit, check, import.' },
  // SET4-001 — its own page, not a resource to pick on the general importer.
  { key: 'gst', label: 'GST-Revised Tax Updates', blurb: "Select the file, validate it and click 'Import' button." },
  // SET1-002 — the Work List tile opens the setup, not the person's own lists.
  { key: 'work_list_setup', label: 'Work list setup', blurb: 'A place to set-up your work list and assign it to roles and users.' },
  { key: 'exports', label: 'Export Data', blurb: 'Every document and master in this company, in one archive.' },
  { key: 'branding', label: 'Branding', blurb: 'The logo on the sign-in page and on printed documents.' },
  { key: 'changelog', label: 'Changelog', blurb: 'Every administrative change: who made it, what it was, and when.' },
  { key: 'delegates', label: 'Delegate User Requests', blurb: 'Temporary support access asked for, approved and revoked.' },
] as const

type Tile = { readonly label: string; readonly blurb: string; readonly to: (companyId: string) => string }

const section = (key: (typeof SECTIONS)[number]['key']): Tile => {
  const s = SECTIONS.find((x) => x.key === key)!
  return { label: s.label, blurb: s.blurb, to: (c) => paths.admin(c, key) }
}
const master = (route: string, label: string, blurb: string): Tile => ({ label, blurb, to: (c) => paths.masterList(c, route) })

/** The reference's Admin hub, in its four groups (SET-004). */
const HUB: ReadonlyArray<{ title: string; tiles: readonly Tile[] }> = [
  { title: 'User and Permission', tiles: [
    section('users'),
    master('roles', 'Roles', 'The named roles you give people; each has its own column of permissions.'),
    section('roles'),
    section('register'),
  ] },
  { title: 'Setup', tiles: [
    { label: 'Settings', blurb: 'How this company works: inventory, numbering, what the rules allow.', to: (c) => paths.settings(c) },
    section('companies'),
    section('numbering'),
    master('currencies', 'Currencies', 'The currencies documents can be written in, and how amounts are shown.'),
    master('warehouses', 'Warehouses', 'Where stock is held.'),
    { label: 'Work List', blurb: 'Set up your work lists and assign them to roles and users.', to: (c) => paths.admin(c, 'work_list_setup') },
    master('file_categories', 'File Categories', 'How files on the drive are sorted, and who sees each kind.'),
    master('collection_types', 'Collection Types', 'The kinds of collection a project drive can hold.'),
    section('workflows'),
    section('imports'),
    section('templates'),
    section('email'),
    section('branding'),
    { ...section('gst'), blurb: 'Change HSN codes and tax rates on many items at once.' },
  ] },
  { title: 'Data', tiles: [section('exports'), section('delegates'), section('changelog'), section('outbox')] },
  { title: 'Integration', tiles: [section('integration')] },
]

export const AdminHub = ({ companyId }: { companyId: string }) => (
  <div className="page">
    <h1 className="page-title">Administration</h1>
    <p className="page-subtitle">The settings that decide who may do what, and what things are called.</p>

    <div className="admin-groups">
      {HUB.map((group) => (
        <Panel key={group.title} title={group.title}>
          <div className="tile-grid">
            {group.tiles.map((tile) => (
              <div className="tile" key={tile.label}>
                <div className="tile__body">
                  <Link to={tile.to(companyId)} className="tile__title">{tile.label}</Link>
                  <p className="tile__desc">{tile.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  </div>
)

export const AdminPage = ({ companyId, section }: { companyId: string; section: string }) => {
  const known = SECTIONS.find((s) => s.key === section)
  if (!known) {
    return (
      <div className="page">
        <h1 className="page-title">Page not found</h1>
        <p className="page-subtitle">There is no administration page at “{section}”.</p>
      </div>
    )
  }

  return (
    <div className="page">
      <Link to={paths.admin(companyId, '')} className="crumb"><Icon name="left" size={15} /> Administration</Link>
      <h1 className="page-title">{known.label}</h1>
      <p className="page-subtitle">{known.blurb}</p>

      {section === 'users' ? <PeoplePage /> : null}
      {section === 'roles' ? <Roles /> : null}
      {section === 'register' ? <LoginRegisterPage /> : null}
      {section === 'delegates' ? <DelegateRequestsPage /> : null}
      {section === 'numbering' ? <NumberingPage /> : null}
      {section === 'workflows' ? <WorkflowsPage /> : null}
      {section === 'companies' ? <CompaniesPage companyId={companyId} /> : null}
      {section === 'templates' ? <PrintTemplates /> : null}
      {section === 'email' ? <EmailTemplates /> : null}
      {section === 'outbox' ? <Outbox /> : null}
      {section === 'integration' ? <IntegrationPage companyId={companyId} /> : null}
      {section === 'imports' ? <DataImports /> : null}
      {section === 'gst' ? <DataImports fixedResource="gst_tax_update" /> : null}
      {section === 'work_list_setup' ? <WorkListSetup /> : null}
      {section === 'exports' ? <DataExports /> : null}
      {section === 'branding' ? <Branding /> : null}
      {section === 'changelog' ? <Changelog /> : null}
    </div>
  )
}

/* ---- roles --------------------------------------------------------------- */

/** What each role may do beyond the documents, enforced in one place. */
const BEYOND_DOCUMENTS = [
  { what: 'Change business settings', owner: true, admin: true, member: false },
  { what: 'Add people and change their roles', owner: true, admin: true, member: false },
  { what: 'Make someone else an owner', owner: true, admin: false, member: false },
]

/**
 * Cancelling and re-opening are the matrix's business now.
 *
 * They used to be gated on the sign-in role alone, which is why the table
 * above could not say anything useful about them.
 */
const BEYOND_NOTE =
  'Approving needs Approve on that document type; cancelling and re-opening need Full access. '
  + 'Master data and reports are rows of the matrix above, under Masters and Reports. '
  + 'The sign-in role only decides who may administer the account — a Member whose business role '
  + 'approves purchase orders can approve them.'


const ROLE_COLUMNS = [
  { role: 'OWNER', label: 'Owner' },
  { role: 'ADMIN', label: 'Administrator' },
  { role: 'MEMBER', label: 'Member' },
]

/**
 * The permission matrix (docs/15 §10.1) and the special actions — see
 * admin/PermissionsPage.tsx — and what the sign-in role alone decides.
 */
const Roles = () => {
  return (
    <>
      <PermissionsPage />

      <Panel title="Everything else">
        <table className="matrix">
          <thead>
            <tr>
              <th scope="col">Action</th>
              {ROLE_COLUMNS.map((c) => <th scope="col" key={c.role}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {BEYOND_DOCUMENTS.map((row) => (
              <tr key={row.what}>
                <th scope="row">{row.what}</th>
                {[row.owner, row.admin, row.member].map((allowed, i) => (
                  <td key={i} className={allowed ? 'matrix__yes' : 'matrix__no'}>{allowed ? 'Yes' : 'No'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: 16 }}>
          {BEYOND_NOTE}
        </p>
        <p className="muted" style={{ marginTop: 10 }}>
          An approval step can also name its own approvers, in which case only those people may sign that step —
          holding the permission is necessary, not sufficient. Whoever submitted a document never signs it
          themselves unless that is switched off in Settings.
        </p>
      </Panel>
    </>
  )
}

/* ---- changing your own password ------------------------------------------- */

export const ChangePasswordPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setDone(true)
      setError(null)
      setCurrent('')
      setNext('')
      setAgain('')
    },
    onError: (err) => setError(messageFor(err)),
  })

  const mismatch = again !== '' && next !== again

  return (
    <div className="page page--narrow">
      <h1 className="page-title">Your password</h1>
      <p className="page-subtitle">Changing it signs out every other device you are signed in on.</p>

      {done ? <div style={{ margin: '18px 0' }}><Notice kind="ok">Your password has been changed.</Notice></div> : null}
      {error ? <div style={{ margin: '18px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="Change it">
        <Field label="Your current password" required>
          <Input type="password" value={current} onChange={setCurrent} />
        </Field>
        <Field label="New password" required hint="At least eight characters, with an uppercase letter, a number and a special character.">
          <Input type="password" value={next} onChange={setNext} />
        </Field>
        <Field label="New password again" required error={mismatch ? 'These two do not match.' : undefined}>
          <Input type="password" value={again} onChange={setAgain} />
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button
            kind="primary"
            disabled={change.isPending || current === '' || next === '' || mismatch}
            onClick={() => change.mutate()}
          >
            Change my password
          </Button>
          <Button kind="ghost" onClick={() => navigate(paths.home(companyId))}>Back</Button>
        </div>
      </Panel>
    </div>
  )
}

/* ============================== changelog ================================= */

/**
 * `Changelog` — docs/11 §Permissions, docs/15 R-10.1, docs/16 AC-1.6.
 *
 * Every one of these entries was already being written and none of them could
 * be read. The question this answers — "who gave them approval rights, and
 * when?" — gets asked after something has gone wrong, by somebody who has no
 * way to reach the database.
 *
 * Nothing here can be edited: the application role holds no UPDATE or DELETE
 * on the audit table, so the screen is telling the truth about what it is.
 */
const Changelog = () => {
  const api = useApi()
  const [entity, setEntity] = useState('')
  const entries = useQuery({
    queryKey: ['changelog', api.companyId, entity],
    queryFn: () => api.adminChangelog(entity === '' ? {} : { entity }),
  })

  if (entries.isLoading) return <Loading />
  if (entries.error) return <ErrorBlock error={entries.error} />

  const rows = entries.data ?? []
  // The filter offers what is actually in the log, rather than a fixed list
  // that goes stale the moment an entity stops being audited.
  const kinds = [...new Map(rows.map((r) => [r.entity, r.entityLabel])).entries()]

  const columns: ReadonlyArray<ColumnDef<ChangelogEntry>> = [
    { key: 'at', header: 'When', width: 190, render: (r) => formatDateTime(r.at) },
    { key: 'actorName', header: 'Who', width: 200 },
    { key: 'entityLabel', header: 'What', width: 180 },
    { key: 'actionLabel', header: 'Change', width: 190 },
    {
      key: 'detail',
      header: 'Detail',
      render: (r) => <ChangeDetail entry={r} />,
    },
  ]

  return (
    <Panel
      title="What has been changed"
      aside={
        <div style={{ minWidth: 220 }}>
          <Select
            value={entity}
            onChange={setEntity}
            placeholder="Everything"
            options={kinds.map(([value, label]) => ({ value, label }))}
          />
        </div>
      }
    >
      <p className="helper">
        The most recent 200 changes. This cannot be edited or removed by anyone, including an
        owner — the application has no permission to write over it.
      </p>
      <Table
        columns={columns}
        rows={rows}
        getKey={(row) => row.id}
        empty={<Empty title="Nothing has been changed yet" body={['Administrative changes appear here as they are made.']} />}
      />
    </Panel>
  )
}

/** What actually changed, in the fewest words that still say it. */
const ChangeDetail = ({ entry }: { entry: ChangelogEntry }) => {
  const before = entry.before ?? {}
  const after = entry.after ?? {}
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  if (keys.length === 0) return <span className="muted">—</span>

  const write = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  return (
    <>
      {keys.slice(0, 4).map((key) => (
        <div key={key} className="cell-sub">
          <strong>{key}</strong>{' '}
          {key in before && write(before[key]) !== write(after[key])
            ? <>{write(before[key])} → {write(after[key])}</>
            : write(after[key] ?? before[key])}
        </div>
      ))}
      {keys.length > 4 ? <div className="cell-sub muted">…and {keys.length - 4} more</div> : null}
    </>
  )
}
